import React from 'react'
import PropTypes from 'prop-types'
import { isInclusivelyBeforeDay } from 'react-dates'

import moment from 'moment'
import Select from 'react-select'
import VMasker from 'vanilla-masker'

import { CustomMenuList as MenuList } from 'component/reactSelect/CustomMenuList'
import { CustomMultiValue as MultiValueContainer } from 'component/reactSelect/CustomMultiValue'
import Col from 'component/utils/Col'
import DatePicker from 'component/utils/newDatePicker'
import Field from 'component/utils/Field'
import Form from 'component/utils/Form'
import HierarchySelect from 'component/hierarchy/HierarchySelect'
import Input from 'component/utils/Input'
import RegionBranchMultipleSelect from 'component/branch/RegionBranchMultipleSelect'
import Row from 'component/utils/Row'

import LittleCheckIcon from 'images/little-check.svg'

const ALL = 'all'

const FIELDS = [
  'branchIdList',
  'cdLm',
  'dtEnd',
  'dtStart',
  'sectionIdList',
  'statusType',
]

const FilterForm = ({
  auth: { user },
  auth: {
    user: { locationCode },
  },
  fields,
  filtersVisible = false,
  setValueToField,
  showTable,
  statusList = [],
  submit,
  translate,
  values,
  exportCsv,
}) => {
  const handleSubmit = (event) => {
    event.preventDefault()

    const initialValues = { ...values }

    const branchIdList =
      locationCode === '999'
        ? values.branchIdList.reduce((acc, cur) => {
            if (cur.idBranches) return [...acc, ...cur.idBranches]

            return ALL !== cur.value ? [...acc, cur.value] : [...acc]
          }, [])
        : [locationCode.padStart(4, '0')]

    const sectionIdList = values.sectionIdList.reduce(
      (acc, cur) => (ALL !== cur.value ? [...acc, cur.value] : [...acc]),
      []
    )

    const params = {
      ...values,
      branchIdList,
      sectionIdList,
      initialValues,
    }

    submit(params)
  }

  const statusOptions = statusList.map(({ code, name }) => ({
    value: code,
    label: name,
  }))

  statusOptions.unshift({ label: translate('option.all') })

  const renderLittleCheck = () => (
    <img
      src={LittleCheckIcon}
      style={{
        position: 'absolute',
        right: '.25rem',
        top: '1.25rem',
        zIndex: 1,
      }}
    />
  )

  const renderFilter = () => (
    <Col xs={12} mdOffset={4} md={4}>
      <h4 className='strong color-default-dark margin-top-quadruple margin-bottom-triple text-align-center'>
        {translate('mobile.filter')}
      </h4>
      {filtersVisible && (
        <Field {...fields.branchIdList} hint={translate('mobile.hint.store')}>
          <RegionBranchMultipleSelect
            {...fields.branchIdList.input}
            allOption
            className='react-select-collapsible'
            components={{ MenuList, MultiValueContainer }}
            id='branchIdList'
            onChange={(event) =>
              setValueToField(
                fields.branchIdList.input.name,
                event.target.value
              )
            }
            placeholder={translate('field.store')}
            translate={translate}
          />
        </Field>
      )}
      <Field {...fields.sectionIdList} hint={translate('mobile.hint.section')}>
        <HierarchySelect
          allOption
          components={{ MenuList, MultiValueContainer }}
          disabled={!!values.cdLm}
          id='sectionIdList'
          isMulti
          name='sectionIdList'
          onChange={(values) => setValueToField('sectionIdList', values || [])}
          placeholder={translate('section')}
          query={{ level: 2 }}
          selected
          translate={translate}
          user={user}
          value={values.sectionIdList}
        />
      </Field>
      <Field {...fields.cdLm} hint={translate('mobile.hint.lm')}>
        <Input
          {...fields.cdLm.input}
          className='default-input'
          disabled={!!values.sectionIdList.length}
          id='cdLm'
          onChange={(event) =>
            setValueToField(
              fields.cdLm.input.name,
              VMasker.toNumber(event.target.value)
            )
          }
          placeholder={translate('lm_label')}
        />
      </Field>
      <Field {...fields.statusType} hint={translate('mobile.hint.status')}>
        <Select
          {...fields.statusType.input}
          className='react-select react-select-multi'
          classNamePrefix='react-select'
          id='statusType'
          onChange={(option) =>
            setValueToField(fields.statusType.input.name, option.value)
          }
          options={statusOptions}
          placeholder={translate('delimit.report.status.placeholder')}
          value={
            statusOptions.find(({ value }) => value === values.statusType) ||
            statusOptions[0]
          }
        />
      </Field>
      <Row>
        <Col xs={6}>
          <Field
            {...fields.dtStart}
            className='margin-bottom-small'
            glyph='glyph glyph-calendar'
            hint={translate('mandatory')}
          >
            <DatePicker
              {...fields.dtStart.input}
              id={fields.dtStart.input.name}
              isOutsideRange={(date) =>
                moment(date).isAfter(values.dtEnd) ||
                !isInclusivelyBeforeDay(date, moment())
              }
              openUp='up'
              placeholder={translate('field.from')}
              scrollIntoView={false}
              showClearDate
            />
            {!!values.dtStart && renderLittleCheck()}
          </Field>
        </Col>
        <Col xs={6}>
          <Field
            {...fields.dtEnd}
            className='margin-bottom-small'
            glyph='glyph glyph-calendar'
            hint={translate('mandatory')}
          >
            <DatePicker
              {...fields.dtEnd.input}
              id={fields.dtEnd.input.name}
              isOutsideRange={(date) =>
                moment(date).isBefore(values.dtStart) ||
                !isInclusivelyBeforeDay(date, moment())
              }
              openUp='up'
              placeholder={translate('field.to')}
              scrollIntoView={false}
              showClearDate
            />
            {!!values.dtEnd && renderLittleCheck()}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className='margin-top-double margin-bottom'>
          <button
            className='button button-primary button-full'
            disabled={!values.dtEnd || !values.dtStart}
            onClick={handleSubmit}
            role='button'
            type='button'
          >
            {translate('filter_button_label')}
          </button>
        </Col>
      </Row>
    </Col>
  )

  const renderTableUpperSection = () => (
    <button
      className='button button-full button-primary margin-top pull-right'
      onClick={exportCsv}
      type='button'
    >
      {translate('mobile.report.csv')}
    </button>
  )

  return (
    <div
      className={`filter-adjustment ${showTable ? '' : 'filter-vertical'}`}
      style={{
        float: showTable ? 'right' : null,
        width: showTable ? '338px' : null,
      }}
    >
      {showTable ? renderTableUpperSection() : renderFilter()}
    </div>
  )
}

FilterForm.propTypes = {
  auth: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  filtersVisible: PropTypes.bool,
  setValueToField: PropTypes.func.isRequired,
  showTable: PropTypes.bool.isRequired,
  statusList: PropTypes.array.isRequired,
  submit: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  exportCsv: PropTypes.func.isRequired,
}

export default Form({ fields: FIELDS })(FilterForm)

export { FilterForm }
